import { useState, useEffect } from 'react';
import GenericSearchBar from '../../../../genericSearchBar/GenericSearchBar';
import { displayErrorNotification, displaySuccessNotification } from '../../../../../utils/displayNudosStandardNotifications';
import { Loading } from '../../../../NudosComponents';
import { getResellersByCountryId, createNewReseller } from '../../../../../services/orders';
import { IResellerInfo, DTOcreateNewReseller } from '../../../../../types/orders';
import useDebounce from '../../../../../hooks/useDebounce';
import { updateProductConfigurationReseller } from '../../../../../services/product-configuration.service';
import { IProductConfigurationUpdateReseller } from '../../../../../types/product.configuration.types';

import './ResellerSearchBar.scss';

interface ResellerSearchBarProps {
  productUuid?: string | null;
  countryCode?: string | null;
  countryId?: number | null;
  reseller?: string | null;
  onResellerSelect?: () => Promise<void>;
}

export const ResellerSearchBar = ({
  productUuid,
  countryId,
  countryCode,
  reseller,
  onResellerSelect
}: ResellerSearchBarProps) => {
  const [searchString, setSearchString] = useState('');
  const [loadingResellers, setLoadingResellers] = useState(true);
  const [openResellerList, setOpenResellerList] = useState(false);
  const [resellersList, setResellersList] = useState<IResellerInfo[]>();
  const [resellerName, setResellerName] = useState<string | undefined>(reseller || '');

  const searchFilter = useDebounce(searchString, 500);

  const handleChangeReseller = async (newReseller?: string, resellerId?: number) => {
    if (!newReseller || !resellerId) return;

    setResellerName(newReseller);
    setSearchString(newReseller);
    setOpenResellerList(false);

    try {
      if (productUuid) {
        await updateProductReseller({ resellerId, reseller: newReseller });
      }
      await onResellerSelect?.();
    } catch (error) {
      console.error('Error updating reseller:', error);
    }
  };

  const searchResellers = async () => {
    if (!searchFilter || searchFilter.length === 0 || !countryId) return;
    setLoadingResellers(true);
    try {
      const resellers = await getResellersByCountryId(countryId);
      if (resellers) {
        const filteredResellers = resellers
          ?.filter(r => r.resellerId.toLowerCase().startsWith(searchFilter.toLowerCase()))
          ?.map(r => ({ resellerId: r.id, reseller: r.resellerId }));

        setResellersList(filteredResellers);
        setLoadingResellers(false);
      }
    } catch (error) {
      displayErrorNotification();
      setLoadingResellers(false);
    }
  };

  const updateProductReseller = async (reseller: IResellerInfo) => {
    if (!productUuid) return;

    try {
      const updateData: IProductConfigurationUpdateReseller = {
        uuid: productUuid,
        resellerId: reseller.resellerId,
        reseller: reseller.reseller
      };

      await updateProductConfigurationReseller(updateData);

      displaySuccessNotification({
        customJSXMessage: <>Reseller ID actualizado exitosamente</>
      });
    } catch (error) {
      displayErrorNotification();
    }
  };

  const createReseller = async () => {
    if (!countryId || !countryCode) return displayErrorNotification();

    const createResellerDto: DTOcreateNewReseller = {
      resellerId: searchString.trim(),
      countryId,
      countryCode
    };

    setLoadingResellers(true);
    try {
      const reseller = await createNewReseller(createResellerDto);
      handleChangeReseller(reseller?.resellerId, reseller?.id);
      setOpenResellerList(false);
    } catch (error) {
      displayErrorNotification();
    } finally {
      setLoadingResellers(false);
    }
  };

  const newResellerOption = (
    <div key="newResellerOption" className="newResellerOption" onClick={createReseller}>
      <div className="searchedText">{searchString} - </div>
      <div className="newText"> Nuevo</div>
    </div>
  );

  const formResellersOptionsUI = () => {
    if (!resellersList || resellersList.length === 0) return undefined;
    const resellersListJSXElements = resellersList?.map((reseller, i) => {
      return (
        <div
          key={`reseller-${reseller.resellerId}-${i}`}
          className="option-container"
          onClick={() => handleChangeReseller(reseller.reseller, reseller.resellerId)}
        >
          <p>{reseller.reseller}</p>
        </div>
      );
    });

    const resellersListAndNewResellerOption = [...resellersListJSXElements, newResellerOption];
    return resellersListAndNewResellerOption;
  };

  const handleResellerSelect = (resellerName: string) => {
    const reseller = resellersList?.find(r => r.reseller === resellerName);
    if (reseller) handleChangeReseller(reseller.reseller, reseller.resellerId);
  };

  const updateResellerData = (reseller: string) => {
    setResellerName(reseller);
    setSearchString(reseller);
  };

  useEffect(() => {
    if (searchFilter && searchFilter) searchResellers();
  }, [searchFilter]);

  useEffect(() => {
    if (reseller) {
      updateResellerData(reseller);
    }
  }, [reseller]);

  return (
    <div className="resellerSearchBarContainer">
      <div className="searchBarTitle">Reseller ID</div>
      <div className="resellerSearchBar">
        <GenericSearchBar
          searchString={searchString}
          setSearchString={setSearchString}
          setIsSelectOpen={setOpenResellerList}
          searchedOptions={formResellersOptionsUI()}
          isSelectOpen={openResellerList}
          initialValue={resellerName || undefined}
          handleChangeSelectedOption={handleResellerSelect}
          noResultsMessage={loadingResellers ? <Loading key="loading" /> : newResellerOption}
          isSearchDisabled={false}
        />
      </div>
    </div>
  );
};
